import { Stack } from "@mui/material";
import { FC, useMemo } from "react";
import { Link, Outlet } from "react-router-dom";
import { Permissions, ReportTemplate, ReportTemplateTypes } from "../../api/dtos";
import GridActions from "../../components/GridActions";
import SetLinks from "../../components/layout/SetLinks";
import { StyledButton } from "../../components/StyledButton";
import CollapsibleAppTable from "../../components/tables/CollapsibleAppTable";
import type { AppColumn } from "../../components/tables/AppTable.types";
import TitleBox from "../../components/TitleBox";
import { StyledLink } from "../../components/utility/StyledLink";
import ReportTemplatesContext from "../../contexts/ReportTemplatesContext";
import usePermissions from "../../hooks/auth/usePermissions";
import useReportTemplates from "../../hooks/data/useReportTemplates";

export const buildEmailOnlyTemplateColumns = (): AppColumn<ReportTemplate>[] => [
    {
        id: "name",
        label: "Name",
        accessor: (row) => row.name,
        scalarType: "text",
        minWidth: 200,
        flex: 1,
    },
];

export const buildPredefinedTemplateColumns = (): AppColumn<ReportTemplate>[] => [
    {
        id: "name",
        label: "Name",
        accessor: (row) => row.name,
        scalarType: "text",
        minWidth: 200,
        flex: 1,
        cell: ({ row, formatted }) => <StyledLink to={`/reports/templates/preview/${row.id}`}>{formatted}</StyledLink>,
    },
];

export const buildCustomTemplateColumns = (): AppColumn<ReportTemplate>[] => [
    {
        id: "name",
        label: "Name",
        accessor: (row) => row.name,
        scalarType: "text",
        minWidth: 200,
        flex: 1,
        cell: ({ row, formatted }) => <StyledLink to={`/reports/templates/edit/${row.id}`}>{formatted}</StyledLink>,
    },
    {
        id: "actions",
        label: "",
        headerLabel: "",
        accessor: () => null,
        scalarType: "text",
        kind: "action",
        width: 100,
        filterable: false,
        sortable: false,
        groupable: false,
        exportable: false,
        cell: ({ row }) => (
            <GridActions
                editUrl={`/reports/templates/edit/${row.id}`}
                deleteUrl={`/reports/templates/delete/${row.id}`}
            />
        ),
    },
];

function createTemplateRowIds(templates: readonly ReportTemplate[]): (row: ReportTemplate) => string | number {
    const fallbackIds = new WeakMap<ReportTemplate, string>();

    templates.forEach((template, index) => fallbackIds.set(template, `report-template-row-${index}`));

    return (row) => row.id ?? fallbackIds.get(row) ?? "report-template-row-unmapped";
}

const ReportTemplates: FC = () => {
    const { data: permissions } = usePermissions();
    const contextValue = useReportTemplates();
    const { data: templates } = contextValue;
    const sourceTemplates = templates ?? [];

    const emailOnlyTemplates = useMemo(
        () =>
            sourceTemplates.filter(
                (template) =>
                    template.templateType === ReportTemplateTypes.EmailOnlyLotSummary ||
                    template.templateType === ReportTemplateTypes.EmailOnlySalesSummary,
            ),
        [templates],
    );

    const predefinedTemplates = useMemo(
        () =>
            sourceTemplates.filter(
                (template) =>
                    template.templateType !== ReportTemplateTypes.Custom &&
                    template.templateType !== ReportTemplateTypes.EmailOnlyLotSummary &&
                    template.templateType !== ReportTemplateTypes.EmailOnlySalesSummary,
            ),
        [templates],
    );

    const customTemplates = useMemo(
        () => sourceTemplates.filter((template) => template.templateType === ReportTemplateTypes.Custom),
        [templates],
    );

    const getRowId = useMemo(() => createTemplateRowIds(sourceTemplates), [templates]);
    const emailOnlyColumns = useMemo(() => buildEmailOnlyTemplateColumns(), []);
    const predefinedColumns = useMemo(() => buildPredefinedTemplateColumns(), []);
    const customColumns = useMemo(() => buildCustomTemplateColumns(), []);
    const predefinedExport = useMemo(
        () => ({
            fileName: "Predefined Report Templates",
            exportTitle: "Predefined Report Templates",
            includeDateTime: true,
        }),
        [],
    );
    const customExport = useMemo(
        () => ({
            fileName: "Custom Report Templates",
            exportTitle: "Custom Report Templates",
            includeDateTime: true,
        }),
        [],
    );
    const canViewCustomReports = permissions?.includes(Permissions.ReportsCanViewCustomReports) ?? false;

    return (
        <ReportTemplatesContext.Provider value={contextValue}>
            <SetLinks stageLinkEnabled={false} lotLinkEnabled={false} />
            <Outlet />
            <TitleBox
                sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                }}
            >
                {canViewCustomReports && (
                    <StyledButton
                        LinkComponent={Link}
                        //@ts-expect-error
                        to="/reports/templates/create"
                    >
                        Create New Template
                    </StyledButton>
                )}
            </TitleBox>
            <Stack gap={2}>
                <CollapsibleAppTable
                    density="compact"
                    title="Email Only Templates"
                    loading={false}
                    columns={emailOnlyColumns}
                    rows={emailOnlyTemplates}
                    getRowId={getRowId}
                    columnControls={{
                        menu: true,
                    }}
                    filterControls
                    export={predefinedExport}
                />
                <CollapsibleAppTable
                    density="compact"
                    title="Predefined Templates"
                    loading={false}
                    columns={predefinedColumns}
                    rows={predefinedTemplates}
                    getRowId={getRowId}
                    columnControls={{
                        menu: true,
                    }}
                    filterControls
                    export={predefinedExport}
                />
                {canViewCustomReports && (
                    <CollapsibleAppTable
                        density="compact"
                        title="Custom Templates"
                        loading={false}
                        columns={customColumns}
                        rows={customTemplates}
                        getRowId={getRowId}
                        columnControls={{
                            menu: true,
                        }}
                        filterControls
                        export={customExport}
                    />
                )}
            </Stack>
        </ReportTemplatesContext.Provider>
    );
};

export default ReportTemplates;
